import React, { useState } from "react";
import LevelRing from "./LevelRing";
import { FaInstagram } from "react-icons/fa";
import { Capacitor } from "@capacitor/core";
import { ordersApi } from "../services/orders";
import LeaderboardFlame from "./LeaderboardFlame";
import { initialsOf } from "../utils/initials";
import { openExternal } from "../lib/openExternal";
import { formatSingleAmount } from "../utils/currency";
import "./FansLeaderboardSheet.css";

// 💰 Top Fan Spenders — platform-wide top 20: the rows of the Fans board on the
// trophy tab (TopFansBoard) and of the talent's fans list (FansModal). Identity is the payment card's Stripe fingerprint (server-
// side); display name is the last nickname/@ig that card used. Amounts are
// each fan's dominant real currency — never blended.

export interface FanRow {
  /** Accounts only (not pre-account history): what opens their card. */
  fanUserId?: string | null;
  name: string;
  // Instagram handle (no @) — makes the name tappable → instagram.com/{ig}.
  // Null when the fan only ever gave a nickname. Once fans connect
  // Instagram by DM (server INSTAGRAM_DM_VERIFY=on) the server sends only
  // connected handles, so the Instagram mark means the handle is theirs.
  ig: string | null;
  // The handle came from Instagram itself ("Verify with a DM").
  igVerified?: boolean;
  // Moderated account profile photo (photos phase) — only account rows
  // ever have one; legacy fingerprint rows render the letter bubble.
  avatarUrl: string | null;
  requests: number;
  spent: number;
  currency: string;
  // Points level (server lib/fanPoints.js): present from level 1 only.
  level?: number;
}

// Row identity bubble: the fan's photo, else their letter on the fan
// gradient (same identity system as the header chip). Shared by the
// fan-page sheet and the DJ-side Top Fans tab.
export const FanRowAvatar: React.FC<{ fan: FanRow }> = ({ fan }) => {
  // A photo URL that 404s (file removed after moderation, stale path) would
  // otherwise paint WebKit's broken-image glyph in the bubble (sim
  // sweep). Fall back to the letter, exactly like a fan with no photo.
  const [broken, setBroken] = useState(false);
  const bubble = fan.avatarUrl && !broken ? (
    <span className="flb-photo">
      <img src={fan.avatarUrl} alt="" referrerPolicy="no-referrer" onError={() => setBroken(true)} />
    </span>
  ) : (
    <span className="flb-photo flb-photo--letter" aria-hidden="true">
      <span className={Array.from(initialsOf(fan.name)).length > 1 ? "flb-initials--two" : undefined}>{initialsOf(fan.name) || "🎧"}</span>
    </span>
  );
  return <LevelRing level={fan.level}>{bubble}</LevelRing>;
};

// Fan name, linked to their Instagram when they claimed one (founder decision).
// Shared by the fan-page sheet and the DJ-side Top Fans tab. In the native
// app the profile opens in the browser sheet, not over the webview. No tick:
// the Instagram mark is the sign (founder, 2026-10-27), and which handles
// reach the board at all is the server's call.
export const FanName: React.FC<{ fan: FanRow }> = ({ fan }) =>
  fan.ig ? (
    <a
      className="flb-name flb-name--ig"
      href={`https://instagram.com/${encodeURIComponent(fan.ig)}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        e.stopPropagation();
        if (Capacitor.isNativePlatform()) {
          e.preventDefault();
          void openExternal(`https://instagram.com/${encodeURIComponent(fan.ig!)}`);
        }
      }}
    >
      {fan.name}
      <FaInstagram className="flb-ig-mark" aria-hidden />
    </a>
  ) : (
    <span className="flb-name">{fan.name}</span>
  );

// Module-level session cache: the board is platform-wide, so reopening it
// (or opening it on another DJ's page) renders instantly. Shared with the
// DJ-side Top Fans tab (TopFansBoard) — one fetch serves both surfaces.
let cached: FanRow[] | null = null;

export function fetchFansLeaderboard(): Promise<FanRow[]> {
  if (cached) return Promise.resolve(cached);
  return ordersApi.fansLeaderboard().then((res) => {
    const rows = Array.isArray(res?.fans) ? res.fans : [];
    const parsed: FanRow[] = rows
      .filter((f: any) => f && typeof f.name === "string" && f.name)
      .slice(0, 20)
      .map((f: any) => ({
        fanUserId: typeof f.fanUserId === "string" ? f.fanUserId : null,
        name: f.name,
        ig: typeof f.ig === "string" && f.ig.trim() ? f.ig.trim().replace(/^@+/, "") : null,
        igVerified: f.igVerified === true,
        avatarUrl:
          typeof f.avatarUrl === "string" && f.avatarUrl.trim() ? f.avatarUrl : null,
        requests: Number(f.requests) || 0,
        spent: Number(f.spent) || 0,
        currency: String(f.currency || "usd"),
        // Points level: only a whole number from 1 up draws a ring.
        ...(Number(f.level) >= 1 ? { level: Math.floor(Number(f.level)) } : {}),
      }));
    cached = parsed;
    return parsed;
  });
}

// One board row, the email chart look (founder pick A, build 44): gold /
// silver / bronze rank numbers (the 🥇🥈🥉 emoji are gone), #1 glows green
// with a flame by the amount. Shared by this sheet and the Top Fans tab.
export const FanBoardRow: React.FC<{ fan: FanRow; i: number; sub: string; onOpen?: (fan: FanRow) => void }> = ({ fan, i, sub, onOpen }) => (
  <li className={`flb-row${i === 0 ? " flb-row--top" : ""}`} style={{ "--i": i } as React.CSSProperties}>
    {/* A fan with an account opens their card; Instagram is one tap further, on it. */}
    {onOpen && fan.fanUserId && <button type="button" className="flb-row-open" aria-label={fan.name} onClick={() => onOpen(fan)} />}
    <span className={`flb-rank${i < 3 ? ` flb-rank--${i + 1}` : ""}`}>{i + 1}</span>
    <FanRowAvatar fan={fan} />
    <span className="flb-meta">
      {onOpen && fan.fanUserId ? <span className="flb-name">{fan.name}</span> : <FanName fan={fan} />}
      <span className="flb-sub">{sub}</span>
    </span>
    <span className="flb-spent">
      {i === 0 && <LeaderboardFlame size={17} live />}
      {formatSingleAmount(fan.spent, fan.currency)}
    </span>
  </li>
);
